/**
 * Pure mutation functions for PCB node actions.
 */

import {
    addPcbNodes,
    handleSetNodeRules,
    hasTopLevelContainerNode,
    removeNodesAndDescendants,
    removeSubjectsFromSelection,
    setElementsAndGenerateNodes,
    toggleNodesWithImportant,
} from "@buildwithflux/core";
import type {AnyPcbBakedNode, AnyPcbNode, IPcbLayoutNodesRuleSetsMap, PcbNode, PcbNodeTypes} from "@buildwithflux/core";
import type {IDocumentData, PcbNodesMap, PcbRule, PcbRulesMap, UserCodeData} from "@buildwithflux/models";
import {isEmpty} from "lodash";

export function applyAddNodes(
    draft: IDocumentData,
    nodes: PcbNodesMap<AnyPcbNode>,
    originalDocument: IDocumentData,
): void {
    if (
        hasTopLevelContainerNode(Object.values(nodes)) &&
        !hasTopLevelContainerNode(Object.values(originalDocument.pcbLayoutNodes)) &&
        originalDocument.elements &&
        !isEmpty(originalDocument.elements)
    ) {
        setElementsAndGenerateNodes(draft, Object.values(originalDocument.elements));
    } else {
        addPcbNodes(draft.pcbLayoutNodes, Object.values(nodes));
    }
}

export function applyAddPolygon(
    draft: IDocumentData,
    node: PcbNode<PcbNodeTypes.polygon>,
    updatedNodePositions: {[uid: string]: PcbRule},
): void {
    addPcbNodes(draft.pcbLayoutNodes, [node]);
    for (const [uid, position] of Object.entries(updatedNodePositions)) {
        const elementNode = draft.pcbLayoutNodes[uid];
        if (elementNode) {
            elementNode.pcbNodeRuleSet.position = position;
        }
    }
}

export function applyRemoveNodes(
    draft: IDocumentData,
    removeNodeUids: string[],
    toggleBakedNodes: AnyPcbBakedNode[],
    updatedNodesRulesSetsMap: IPcbLayoutNodesRuleSetsMap,
): void {
    if (!isEmpty(updatedNodesRulesSetsMap)) {
        handleSetNodeRules(draft, updatedNodesRulesSetsMap);
    }
    removeNodesAndDescendants(draft.pcbLayoutNodes, removeNodeUids);
    removeSubjectsFromSelection(draft, removeNodeUids);
    if (toggleBakedNodes.length > 0) {
        toggleNodesWithImportant(draft, toggleBakedNodes);
    }
}

export function applySetNodeLabel(draft: IDocumentData, nodeUid: string, label: string): void {
    const draftNode = draft.pcbLayoutNodes[nodeUid];
    if (draftNode) {
        draftNode.name = label;
    }
}

export function applySetNodeLabelMulti(draft: IDocumentData, nodeUidLabelMap: {[nodeUid: string]: string}): void {
    for (const [nodeUid, label] of Object.entries(nodeUidLabelMap)) {
        const node = draft.pcbLayoutNodes[nodeUid];
        if (node) {
            node.name = label;
        }
    }
}

export function applySetNodeUserData(draft: IDocumentData, nodeUid: string, userData: UserCodeData): void {
    const draftNode = draft.pcbLayoutNodes[nodeUid];
    if (draftNode) {
        draftNode.userCodeData = userData;
    }
}

export function applySetNodeRulesMulti(draft: IDocumentData, nodeUidRulesMap: {[nodeUid: string]: PcbRulesMap}): void {
    for (const [nodeUid, rules] of Object.entries(nodeUidRulesMap)) {
        const node = draft.pcbLayoutNodes[nodeUid];
        if (node) {
            node.pcbNodeRuleSet = rules;
        }
    }
}

export function applySetRuleSetSelector(draft: IDocumentData, ruleSetUid: string, selector: string): void {
    const draftRuleSet = draft.pcbLayoutRuleSets[ruleSetUid];
    if (draftRuleSet) {
        draftRuleSet.selector = selector;
    }
}

export function applySetRuleSetRules(draft: IDocumentData, ruleSetUid: string, rules: PcbRulesMap): void {
    const draftRuleSet = draft.pcbLayoutRuleSets[ruleSetUid];
    if (draftRuleSet) {
        draftRuleSet.rules = rules;
    }
}
